import * as duckdb from '@duckdb/duckdb-wasm';
import { logger } from '@/utils/logger';

let persistentConn = null;
let duckDBInitPromise = null;
let _isInitializing = false;

/**
 * Initialize DuckDB WASM instance (no tables or views created yet).
 * Views are created lazily when parquet files are registered via loadFilesIntoDuckDB().
 * Uses a promise lock to prevent duplicate initializations from concurrent calls.
 * @param {import('vue').Ref} duckDBInstanceRef - Vue ref holding the DuckDB instance
 */
export const initializeDuckDB = async (duckDBInstanceRef) => {
  if (duckDBInstanceRef.value) return;
  if (_isInitializing) return duckDBInitPromise;

  _isInitializing = true;
  duckDBInitPromise = (async () => {
    logger.log('Starting DuckDB initialization (in-memory)');

    try {
      const JSDELIVR_BUNDLES = await duckdb.selectBundle({
        mvp: {
          mainModule: '/duckdb/duckdb-mvp.wasm',
          mainWorker: '/duckdb/duckdb-browser-mvp.worker.js',
        },
        eh: {
          mainModule: '/duckdb/duckdb-eh.wasm',
          mainWorker: '/duckdb/duckdb-browser-eh.worker.js',
        }
      });

      const worker = new Worker(JSDELIVR_BUNDLES.mainWorker);
      const dbLogger = new duckdb.VoidLogger();
      const db = new duckdb.AsyncDuckDB(dbLogger, worker);
      await db.instantiate(JSDELIVR_BUNDLES.mainModule);

      duckDBInstanceRef.value = db;
      logger.log('DuckDB initialized successfully');
    } catch (error) {
      duckDBInitPromise = null;
      throw error;
    } finally {
      _isInitializing = false;
    }
  })();

  return duckDBInitPromise;
};

/**
 * Get or create a persistent DuckDB connection.
 * Reuses a single connection across all queries.
 * @param {import('vue').Ref} duckDBInstanceRef - Vue ref holding the DuckDB instance
 */
export const getConnection = async (duckDBInstanceRef) => {
  if (!duckDBInstanceRef.value) {
    throw new Error('DuckDB not initialized');
  }
  if (!persistentConn) {
    persistentConn = await duckDBInstanceRef.value.connect();
  }
  return persistentConn;
};

/**
 * Close the persistent connection (call on sign-out or cleanup).
 */
export const closeConnection = async () => {
  if (persistentConn) {
    try {
      await persistentConn.close();
    } catch (_) {
      // Ignore close errors
    }
    persistentConn = null;
  }
};

/**
 * Executes a query against DuckDB using a persistent connection.
 * Retries once on connection error (stale connection recovery).
 * @param {import('vue').Ref} duckDBInstanceRef - Vue ref holding the DuckDB instance
 * @param {string} sql - SQL query to execute
 * @param {Array} params - Query parameters
 * @returns {Promise<Array>} Query results
 */
export const queryDuckDB = async (duckDBInstanceRef, sql, params = []) => {
  if (!duckDBInstanceRef.value) {
    throw new Error('DuckDB not initialized');
  }

  try {
    const conn = await getConnection(duckDBInstanceRef);
    return await conn.query(sql, params);
  } catch (err) {
    // Retry once: reset connection and try again
    persistentConn = null;
    try {
      const conn = await getConnection(duckDBInstanceRef);
      return await conn.query(sql, params);
    } catch (retryErr) {
      logger.error('Query failed after retry:', retryErr);
      throw retryErr;
    }
  }
};

/**
 * Reset module state (for testing).
 */
export const _resetForTesting = () => {
  persistentConn = null;
  duckDBInitPromise = null;
  _isInitializing = false;
};
